// This is the React component for Section 8 ("A Learning Journey")
// It's a mirror of Section 5, with the paper rip aligned to the right.

import React, { useLayoutEffect, useRef } from "react";
import "./Section8.css"; // We will create this file next
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Import the paper rip image.
// The main background (0_1.jpg) is handled in CSS.
import paperRipImg from "../../../assets/images/PaperRip2.png";

gsap.registerPlugin(ScrollTrigger);

// --- Component Text ---
const titleText = "A Learning Journey";
const paragraphText1 =
  "The Dandelion Run Community embodies the resilience and adaptability of its namesake. It provides a structured yet flexible environment where youth can thrive together through movement.";


// --- React Component ---
function Section8() {
  // --- Refs ---
  const sectionRef = useRef(null); // The main section
  const ripContainerRef = useRef(null); // The paper rip element
  const titleRef = useRef(null); // The title
  const paragraph1Ref = useRef(null); // First paragraph
  const paragraph2Ref = useRef(null); // Second paragraph

  // --- Animation ---
  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      // We'll animate the title and paragraphs together
      const textElements = [
        titleRef.current,
        paragraph1Ref.current,
        paragraph2Ref.current,
      ];

      // Create a timeline for our 2-part sequence
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 65%", // Starts when the section is 75% from the top
          toggleActions: "play none none none",
          once: true,
        },
      });

      // --- Animation 1: The Rip Reveals Downwards ---
      tl.fromTo(
        ripContainerRef.current,
        { clipPath: "inset(0 0 100% 0)" }, // from: hidden
        {
          clipPath: "inset(0 0 0% 0)", // to: fully visible
          duration: 1.2, // A little slower for a nice wipe
          ease: "power3.out",
        }
      );

      // --- Animation 2: The Text Reveals from the Left ---
      tl.fromTo(
        textElements, // Animate all 3 text elements
        { clipPath: "inset(0 100% 0 0)" }, // from: hidden
        {
          clipPath: "inset(0 0% 0 0)", // to: fully visible
          duration: 1,
          stagger: 0.2, // Stagger the title, then p1, then p2
          ease: "power2.out",
        },
        "-=0.7" // Starts 0.7s *before* the rip animation finishes
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  // --- JSX (HTML) ---
  return (
    // The main background (0_1.jpg) is applied by this class
    <section className="section-8" ref={sectionRef}>
      
      {/* This container holds the rip image and the text content. */}
      <div className="section-8-rip-container" ref={ripContainerRef}>
        
        {/* This is the PaperRip1.png image */}
        {/* It has a 'flip' class to mirror the image */}
        <img
          src={paperRipImg}
          alt="Torn paper background"
          className="section-8-rip-bg"
        />

        {/* This div holds the text and sits *on top* of the rip PNG */}
        <div className="section-8-content">
          <h2 className="section-8-title" ref={titleRef}>
            {titleText}
          </h2>

          {/* We attach refs to each paragraph for the animation */}
          <p className="section-8-paragraph" ref={paragraph1Ref}>
            {paragraphText1}
          </p>
        </div>
        
      </div>
    </section>
  );
}

export default Section8;